import { Chart, registerables } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { Panel } from '../../../../components/panels/Panel';
import { formatVeralliaDashboardSize } from '../../../../services/dashboard/verallia-dashboard-service';
import { VeralliaDashboardNoData } from './VeralliaDashboardBlocks';

Chart.register(...registerables);

export const graphColors = [
  'rgb(255, 99, 132)',
  'rgb(75, 192, 192)',
  'rgb(255, 159, 64)',
  'rgb(153, 102, 255)',
  'rgb(255, 153, 153)',
  'rgb(54, 162, 235)',
  'rgb(255, 205, 86)',
  'rgb(153, 255, 153)',
  'rgb(255, 204, 153)',
  'rgb(153, 204, 255)',
  'rgb(255, 255, 153)',
  'rgb(204, 153, 255)',
  'rgb(255, 102, 102)',
  'rgb(102, 255, 255)',
  'rgb(204, 204, 204)',
];

const SHIFT_WARNING_COLOR = '#F28725';

const getCommonScale = () => ({
  beginAtZero: true,
  border: { display: false },
  ticks: { color: '#777777' },
  grid: { color: '#333333' },
});

export const VeralliaDashboardDefectClassification = ({ classification, fixedHeight = false }) => {
  const panelHeight = fixedHeight ? 'h-[360px]' : 'full';

  if (classification.length === 0) {
    return (
      <Panel noHeader rounded category="secondary" overflowAuto={false} height={panelHeight}>
        <div className="mb-4 shrink-0 text-sm font-semibold uppercase text-perception-gray-400">Defect classification by quantity</div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <VeralliaDashboardNoData/>
        </div>
      </Panel>
    );
  }

  const maxCount = Math.max(...classification.map(({ count }) => count));

  return (
    <Panel noHeader rounded category="secondary" overflowAuto={false} height={panelHeight}>
      <div className="mb-4 flex shrink-0 items-center justify-between">
        <div className="text-sm font-semibold uppercase text-perception-gray-400">Defect classification by quantity</div>
        <div className="text-xs text-perception-gray-600">{classification.reduce((sum, { count }) => sum + count, 0)} detections</div>
      </div>

      <div className={fixedHeight ? 'min-h-0 flex-1 overflow-y-auto pr-2' : 'flex flex-col gap-4'}>
        <div className="flex flex-col gap-4">
          {classification.map((defect, index) => (
            <div key={defect.id} className="flex flex-col gap-2">
              <div className="flex justify-between gap-4 text-sm">
                <div className="flex gap-2">
                  <span className="text-perception-gray-700">#{String(index + 1).padStart(2, '0')}</span>
                  <span className="text-perception-gray-300">{defect.name}</span>
                </div>

                <div className="flex gap-2">
                  <span className="text-perception-gray-300">{defect.count}</span>
                  <span className="text-perception-gray-600">{defect.share.toFixed(1)}%</span>
                </div>
              </div>

              <div className="h-2 overflow-hidden rounded-full bg-perception-black-800">
                <div className="h-full bg-perception-blue" style={{ width: `${maxCount > 0 ? defect.count / maxCount * 100 : 0}%` }}/>
              </div>

              <div className="text-xs text-perception-gray-600">Avg size: {formatVeralliaDashboardSize(defect.averageSize)}</div>
            </div>
          ))}
        </div>
      </div>
    </Panel>
  );
};

export const VeralliaDashboardBarChart = ({ title, labels, values, subtitle, color = graphColors[5] }) => {
  const hasData = values.some((value) => value > 0);

  const data = {
    labels,
    datasets: [{
      label: 'Detections',
      data: values,
      backgroundColor: color,
      borderRadius: 4,
    }],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: getCommonScale(),
      y: getCommonScale(),
    },
  };

  return (
    <Panel noHeader rounded category="secondary" overflowAuto={false}>
      <div className="mb-4 flex items-center justify-between">
        <div className="text-sm font-semibold uppercase text-perception-gray-400">{title}</div>
        {subtitle && <div className="text-xs text-perception-gray-600">{subtitle}</div>}
      </div>

      <div className="h-64">
        {hasData ? <Bar data={data} options={options}/> : <VeralliaDashboardNoData/>}
      </div>
    </Panel>
  );
};

export const VeralliaDashboardShiftEvolutionChart = ({ evolution }) => {
  const hasData = evolution.hourly.some((value) => value > 0);

  const data = {
    labels: evolution.labels,
    datasets: [
      {
        type: 'bar',
        label: 'Detections / 30 min',
        data: evolution.hourly,
        backgroundColor: SHIFT_WARNING_COLOR,
        borderRadius: 3,
        barThickness: 14,
        maxBarThickness: 14,
        categoryPercentage: 1,
        barPercentage: 0.85,
        yAxisID: 'y',
      },
      {
        type: 'line',
        label: 'Cumulative detections',
        data: evolution.cumulative,
        borderColor: 'rgba(255, 255, 255, 0.95)',
        backgroundColor: 'rgba(255, 255, 255, 0.14)',
        pointBackgroundColor: 'rgba(255, 255, 255, 1)',
        pointBorderColor: 'rgba(255, 255, 255, 1)',
        pointRadius: 2,
        pointHoverRadius: 4,
        borderWidth: 2.5,
        tension: 0.3,
        fill: true,
        yAxisID: 'y1',
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: {
        labels: {
          color: '#929292',
          boxWidth: 12,
          boxHeight: 12,
        },
      },
    },
    scales: {
      x: {
        ...getCommonScale(),
        ticks: {
          color: '#777777',
          autoSkip: false,
          maxRotation: 45,
          minRotation: 45,
        },
      },
      y: {
        ...getCommonScale(),
        position: 'left',
        title: {
          display: true,
          text: 'Detections / 30 min',
          color: '#777777',
        },
      },
      y1: {
        ...getCommonScale(),
        position: 'right',
        grid: { drawOnChartArea: false },
        title: {
          display: true,
          text: 'Cumulative',
          color: '#777777',
        },
      },
    },
  };

  return (
    <Panel noHeader rounded category="secondary" overflowAuto={false} height="h-[360px]">
      <div className="mb-4 shrink-0 text-sm font-semibold uppercase text-perception-gray-400">Shift evolution</div>

      <div className="min-h-0 flex-1">
        {hasData ? <Bar data={data} options={options}/> : <VeralliaDashboardNoData/>}
      </div>
    </Panel>
  );
};